import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    fetchStoreSortData,
    saveStoreCategorySort,
    saveStoreItemSort,
} from "../service/api/adminApi";
import "../css/StoreSortPage.css";

export default function StoreSortPage() {
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [savingType, setSavingType] = useState("");
    const [toast, setToast] = useState("");
    const [error, setError] = useState("");

    const [store, setStore] = useState(null);
    const [categories, setCategories] = useState([]);
    const [itemsByCategory, setItemsByCategory] = useState({});
    const [activeCategoryId, setActiveCategoryId] = useState(null);

    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [dragItem, setDragItem] = useState(null);
    const [dragCategoryId, setDragCategoryId] = useState(null);

    const itemDragRef = useRef(null);
    const categoryDragRef = useRef(null);
    const itemMovedRef = useRef(false);
    const categoryMovedRef = useRef(false);
    const itemsRef = useRef({});
    const categoriesRef = useRef([]);

    const activeCategory = useMemo(
        () => categories.find((c) => String(c.id) === String(activeCategoryId)),
        [categories, activeCategoryId]
    );

    const activeItems = useMemo(
        () => itemsByCategory?.[activeCategoryId] || [],
        [itemsByCategory, activeCategoryId]
    );

    useEffect(() => {
        itemsRef.current = itemsByCategory;
    }, [itemsByCategory]);

    useEffect(() => {
        categoriesRef.current = categories;
    }, [categories]);

    const showToast = (message) => {
        setToast(message);
        window.clearTimeout(window.__sspToastTimer);
        window.__sspToastTimer = window.setTimeout(() => setToast(""), 2200);
    };

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");

            const res = await fetchStoreSortData(id);

            if (res?.success) {
                const nextCategories = res.data?.categories || [];
                const nextItems = res.data?.items_by_category || {};

                setStore(res.data?.store || null);
                setCategories(nextCategories);
                setItemsByCategory(nextItems);
                setActiveCategoryId((prev) => {
                    const exists = nextCategories.find((c) => String(c.id) === String(prev));
                    return exists?.id || nextCategories?.[0]?.id || null;
                });
            }
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || "Failed to load sort data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const reorder = (list, fromId, toId) => {
        const fromIndex = list.findIndex((x) => String(x.id) === String(fromId));
        const toIndex = list.findIndex((x) => String(x.id) === String(toId));

        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return list;

        const next = [...list];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
    };

    const saveItems = async (categoryId, nextItems) => {
        try {
            setSavingType(`items-${categoryId}`);
            await saveStoreItemSort(id, nextItems.map((item) => item.id));
            showToast(" ✅ Items sorted successfully");
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || "Failed to save item order");
            loadData();
        } finally {
            setSavingType("");
        }
    };

    const saveCategories = async (nextCategories) => {
        try {
            setSavingType("categories");
            await saveStoreCategorySort(id, nextCategories.map((category) => category.id));
            showToast(" ✅ Menu categories sorted successfully");
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || "Failed to save category order");
            loadData();
        } finally {
            setSavingType("");
        }
    };

    const handleItemDrop = async (targetItemId) => {
        if (!dragItem || String(dragItem.categoryId) !== String(activeCategoryId)) {
            setDragItem(null);
            return;
        }

        const current = itemsByCategory[activeCategoryId] || [];
        const next = reorder(current, dragItem.itemId, targetItemId);

        setDragItem(null);

        if (next === current) return;

        setItemsByCategory((prev) => ({
            ...prev,
            [activeCategoryId]: next,
        }));

        await saveItems(activeCategoryId, next);
    };

    const handleCategoryDrop = async (targetCategoryId) => {
        if (!dragCategoryId) return;

        const next = reorder(categories, dragCategoryId, targetCategoryId);
        setDragCategoryId(null);

        if (next === categories) return;

        setCategories(next);
        await saveCategories(next);
    };

    const handleItemPointerDown = (event, itemId) => {
        if (event.pointerType === "mouse") return;

        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);

        itemDragRef.current = {
            itemId,
            categoryId: activeCategoryId,
        };

        itemMovedRef.current = false;
        setDragItem({ itemId, categoryId: activeCategoryId });
    };

    const handleItemPointerMove = (event) => {
        const source = itemDragRef.current;
        if (!source) return;

        event.preventDefault();

        const target = document
            .elementFromPoint(event.clientX, event.clientY)
            ?.closest("[data-item-drag-id]");

        const targetItemId = target?.dataset?.itemDragId;
        if (!targetItemId || String(targetItemId) === String(source.itemId)) return;

        setItemsByCategory((current) => {
            const list = current[source.categoryId] || [];
            const nextList = reorder(list, source.itemId, targetItemId);

            if (nextList === list) return current;

            itemMovedRef.current = true;
            itemsRef.current = {
                ...current,
                [source.categoryId]: nextList,
            };

            return itemsRef.current;
        });
    };

    const finishItemPointerDrag = async (event) => {
        const source = itemDragRef.current;
        if (!source) return;

        event.preventDefault();
        event.currentTarget.releasePointerCapture?.(event.pointerId);

        const shouldSave = itemMovedRef.current;

        itemDragRef.current = null;
        itemMovedRef.current = false;
        setDragItem(null);

        if (shouldSave) {
            await saveItems(source.categoryId, itemsRef.current[source.categoryId] || []);
        }
    };

    const handleCategoryPointerDown = (event, categoryId) => {
        if (event.pointerType === "mouse") return;

        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);

        categoryDragRef.current = categoryId;
        categoryMovedRef.current = false;
        setDragCategoryId(categoryId);
    };

    const handleCategoryPointerMove = (event) => {
        const sourceId = categoryDragRef.current;
        if (!sourceId) return;

        event.preventDefault();

        const target = document
            .elementFromPoint(event.clientX, event.clientY)
            ?.closest("[data-category-drag-id]");

        const targetCategoryId = target?.dataset?.categoryDragId;
        if (!targetCategoryId || String(targetCategoryId) === String(sourceId)) return;

        setCategories((current) => {
            const next = reorder(current, sourceId, targetCategoryId);

            if (next === current) return current;

            categoryMovedRef.current = true;
            categoriesRef.current = next;
            return next;
        });
    };

    const finishCategoryPointerDrag = async (event) => {
        const sourceId = categoryDragRef.current;
        if (!sourceId) return;

        event.preventDefault();
        event.currentTarget.releasePointerCapture?.(event.pointerId);

        const shouldSave = categoryMovedRef.current;

        categoryDragRef.current = null;
        categoryMovedRef.current = false;
        setDragCategoryId(null);

        if (shouldSave) {
            await saveCategories(categoriesRef.current);
        }
    };

    if (loading) {
        return (
            <div className="ssp-page">
                <div className="ssp-skeleton-head" />
                <div className="ssp-shell">
                    <div className="ssp-sidebar">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="ssp-skeleton-line" />
                        ))}
                    </div>
                    <div className="ssp-content">
                        {Array.from({ length: 9 }).map((_, i) => (
                            <div key={i} className="ssp-skeleton-card" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error && !categories.length) {
        return (
            <div className="ssp-page">
                <div className="ssp-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="ssp-page">
            {toast && <div className="ssp-toast">{toast}</div>}

            <div className="ssp-header">
                <div>
                    <h2>
                        Sorting Menu and Items for:{" "}
                        <span>{store?.name || `Restaurant #${id}`}</span>
                    </h2>
                    <p>Drag and drop items or categories. Order saves automatically.</p>
                </div>

                <button
                    type="button"
                    className="ssp-category-btn"
                    onClick={() => setCategoryModalOpen(true)}
                >
                    Sort Menu Categories
                </button>
            </div>

            {error && (
                <div className="ssp-alert ssp-alert-error">
                    {error}
                    <button type="button" onClick={() => setError("")}>
                        ×
                    </button>
                </div>
            )}

            <div className="ssp-shell">
                <aside className="ssp-sidebar">
                    <div className="ssp-sidebar-title">Menu Categories</div>

                    {categories.length === 0 ? (
                        <div className="ssp-empty">No categories found.</div>
                    ) : (
                        categories.map((category) => (
                            <button
                                key={category.id}
                                type="button"
                                className={`ssp-category-tab ${
                                    String(activeCategoryId) === String(category.id) ? "active" : ""
                                }`}
                                onClick={() => setActiveCategoryId(category.id)}
                            >
                                {category.name}
                            </button>
                        ))
                    )}
                </aside>

                <main className="ssp-content">
                    <div className="ssp-content-head">
                        <div>
                            <h3>
                                Sort Items in:{" "}
                                <span>{activeCategory?.name || "Selected Category"}</span> menu
                            </h3>
                            <p>{activeItems.length} items</p>
                        </div>

                        {savingType === `items-${activeCategoryId}` && (
                            <div className="ssp-saving">Saving...</div>
                        )}
                    </div>

                    {activeItems.length === 0 ? (
                        <div className="ssp-empty ssp-empty-large">
                            No items found in this category.
                        </div>
                    ) : (
                        <div className="ssp-items-grid">
                            {activeItems.map((item) => (
                                <div
                                    key={item.id}
                                    className={`ssp-item-card ${
                                        String(dragItem?.itemId) === String(item.id)
                                            ? "is-touch-dragging"
                                            : ""
                                    }`}
                                    data-item-drag-id={item.id}
                                    draggable
                                    onDragStart={() =>
                                        setDragItem({
                                            categoryId: activeCategoryId,
                                            itemId: item.id,
                                        })
                                    }
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => handleItemDrop(item.id)}
                                    onDragEnd={() => setDragItem(null)}
                                    onPointerDown={(event) => handleItemPointerDown(event, item.id)}
                                    onPointerMove={handleItemPointerMove}
                                    onPointerUp={finishItemPointerDrag}
                                    onPointerCancel={finishItemPointerDrag}
                                >
                                    {item.image_url ? (
                                        <img src={item.image_url} alt={item.name} />
                                    ) : (
                                        <div className="ssp-no-img">No Image</div>
                                    )}

                                    <div className="ssp-item-info">
                                        <strong>{item.name}</strong>
                                        <span>₹{Number(item.price || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>

            {categoryModalOpen && (
                <div className="ssp-modal-overlay">
                    <div className="ssp-modal">
                        <div className="ssp-modal-head">
                            <h3>Sort Menu Categories</h3>
                            <button
                                type="button"
                                className="ssp-modal-close"
                                onClick={() => setCategoryModalOpen(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="ssp-modal-body">
                            <div className="ssp-category-sort-list">
                                {categories.map((category) => (
                                    <div
                                        key={category.id}
                                        className={`ssp-category-drag-card ${
                                            String(dragCategoryId) === String(category.id)
                                                ? "is-touch-dragging"
                                                : ""
                                        }`}
                                        data-category-drag-id={category.id}
                                        draggable
                                        onDragStart={() => setDragCategoryId(category.id)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => handleCategoryDrop(category.id)}
                                        onDragEnd={() => setDragCategoryId(null)}
                                        onPointerDown={(event) =>
                                            handleCategoryPointerDown(event, category.id)
                                        }
                                        onPointerMove={handleCategoryPointerMove}
                                        onPointerUp={finishCategoryPointerDrag}
                                        onPointerCancel={finishCategoryPointerDrag}
                                    >
                                        <span className="ssp-drag-handle">☰</span>
                                        <span>{category.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="ssp-modal-foot">
              <span>
                {savingType === "categories"
                    ? "Saving category order..."
                    : "Drag and drop to reorder"}
              </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}